import 'vue2-animate/dist/vue2-animate.min.css'
import 'tb-skeleton/dist/skeleton.css'
import '@/styles/mobile-normalize.css'
import '@/styles/animate.css'
import '@/styles/transitions.css'
import 'vue-toast-notification/dist/index.css'

import 'web-animations-js'
import matchAll from 'string.prototype.matchall';

import Vue from 'vue'
import VueRouter from 'vue-router'
import VueToast from 'vue-toast-notification'
import skeleton from 'tb-skeleton'
import * as Sentry from '@sentry/browser';
import LogRocket from 'logrocket';
import { Vue as VueIntegration } from '@sentry/integrations';
import axios from 'axios'
import App from '@/App'
import router from '@/router'
import store from '@/stores'
import vueEventCalendar from './components/vue-calendar'
import { InlineSvgPlugin, InlineSvgComponent } from '@/components/InlineSvg'
import TreeView from 'vue-json-tree-view'
import VueAnalytics from 'vue-analytics'
import urljoin from 'url-join'
import LocalStorageUtils from '@/utils/localStorageUtils'
import DeviceUtils from '@/utils/deviceUtils'
import ErrorUtils from '@/utils/errorUtils'
import EnvironmentUtils from '@/utils/environmentUtils'
import UpdateMigrationUtils from '@/utils/updateMigrationUtils'
import VueUtils from '@/utils/vueUtils'
import LogUtils from '@/utils/logUtils'
import ServicesUtils from '@/utils/servicesUtils'
import DOMUtils from '@/utils/DOMUtils'
import AuthUtils from '@/utils/authUtils'
import IdentifyUtils from '@/utils/identifyUtils'
import StringUtils from '@/utils/stringUtils'
import MathUtils from '@/utils/mathUtils'
import FileReaderUtils from '@/utils/fileReaderUtils'
import FileUtils from '@/utils/fileUtils'
import OneSignalUtils from '@/utils/oneSignalUtils'
import TouchUtils from '@/utils/touchUtils'
import ColorUtils from '@/utils/colorUtils'
import ParticlesUtils from '@/utils/particlesUtils'
import CovidPDFUtils from '@/utils/covidPDFUtils'
import StatusServices from '@/services/status'
import RemoteScriptsServices from '@/services/remoteScripts'
import RequestRelayServices from '@/services/requestRelay'
import Theme from '@/theme'
import SplashscreenView from '@/views/Splashscreen'
import Config from '~/config/index'
import Package from '~/package.json'
import UsersServices from '@/services/users'
import ConfigUtils from '@/utils/configUtils'
import BootsrapperUtils from '@/utils/bootstrapperUtils'

matchAll.shim()
window.modules =
{
    DOMUtils,
    UpdateMigrationUtils,
    LocalStorageUtils,
    DeviceUtils,
    StringUtils,
    AuthUtils,
    EnvironmentUtils,
    MathUtils,
    axios,
    RequestRelayServices,
    FileReaderUtils,
    FileUtils,
    TouchUtils,
    VueUtils,
    ColorUtils,
    OneSignalUtils,
    CovidPDFUtils,
    ParticlesUtils,
    Vue,
    BootsrapperUtils,
    Theme,
}

console.log(`%c🏠 Ma Ville v${Package.version} '${Package.release}' | Have fun `, LogUtils.styles.rainbow)
LogUtils.initialize()

Vue.config.productionTip = false
Vue.config.errorHandler = onError

document.addEventListener('deviceready', onDeviceReady, false);
window.addEventListener('error', onError)

Vue.use(VueUtils.LifecycleHooksInterceptorPlugin)
Vue.use(VueRouter)
Vue.use(InlineSvgPlugin)
Vue.use(TreeView)
Vue.use(skeleton)
Vue.use(VueToast, { position: 'top' })
Vue.use(vueEventCalendar, {locale: 'fr', color: Theme.current.accentColorText}) 

Theme.current = Theme.themes[LocalStorageUtils.activeTheme] || Theme.themes.auto

// These svg are used with <inline-svg> which takes some time to render svgs for the first time.
// Without preloading images, it gives a cringy feeling when svg are rendered,
// especially on mobiles where it's clearly visible that the elements are resized as the image contained into is loaded.
const svgsToPreload =
[
    require('@/assets/img/bell.svg'),
    require('@/assets/img/danger.svg'),
    require('@/assets/img/magnifying-glass-thin.svg'),
    require('@/assets/img/smile.svg'),
    require('@/assets/img/speaker.svg'),
    require('@/assets/img/circle-spinner.svg'),
    require('@/assets/img/crying.svg'),
    require('@/assets/img/checked.svg'),
    require('@/assets/img/key-kawaii.svg'),
    require('@/assets/img/key.svg'),
    require('@/assets/img/key-outlined.svg'),
    require('@/assets/img/logout.svg'),
    require('@/assets/img/lock.svg'),
    require('@/assets/img/plus.svg'),
    require('@/assets/img/plus-outlined.svg'),
    require('@/assets/img/pencil-outlined.svg'),
    require('@/assets/img/paint-brush.svg'),
    require('@/assets/img/question.svg'),
    require('@/assets/img/question-mark.svg'),
]

// On Android when fonts are displayed for the first time there is like a 0.8s loading which is ugly.
const fontsToPreload =
[
    { fontWeight: 800 },
    { fontWeight: 600 },
]

function buildApp ()
{
    ConfigUtils.overwriteConfig(Config, LocalStorageUtils.configOverwrite)
    console.log('User UUID:', LocalStorageUtils.userUuid)

    if (EnvironmentUtils.isProductionEnv)
    {
        const sentryRelease = EnvironmentUtils.envCityId + '@' + __BUILD_NUMBER__
        Sentry.init
        ({
            dsn: Config.sentryDsn,
            integrations: [new VueIntegration({Vue, attachProps: true})],
            release: sentryRelease
        })
        console.log('Sentry initialized with release', sentryRelease)

        const baseHref = urljoin(Config.publicAppBaseUrl)
        console.log('Setting LogRocket baseHref to', baseHref)
        LogRocket.init(Config.logRocketAppId, {dom: { baseHref }})
        console.log('LogRocket initialized with app id', Config.logRocketAppId)
    }
    else console.log('Development environment detected, not initializing Sentry & LogRocket')

    const analyticsConfig =
    {
        id: Config.analyticsId,
        fields: { cookieDomain: 'none' },
        router,
        autoTracking: { screenView: true }
    }
    analyticsConfig.fields.userId = LocalStorageUtils.userUuid
    Vue.use(VueAnalytics, analyticsConfig)

    window.app = new Vue({
        el: '#app',
        router,
        store,
        template: '<App :fullscreen-spinner="showFullscreenSpinner" />',
        components: { App },
    
        data ()
        {
            return {
                showFullscreenSpinner: false
            }
        },

        methods:
        {
        },
    
        created: onAppCreated,
        mounted: onAppMounted
    })

    // Fixes issues with app loaded through file:// protocol
    app.$ga.set('checkProtocolTask', null)
    app.$ga.set('checkStorageTask', null)
    app.$ga.set('historyImportTask', null)
}

function onAppCreated ()
{
    for (const path of svgsToPreload) InlineSvgComponent.methods.cacheImage(path)

    const container = document.createElement('div')
    container.style.color = 'transparent'
    document.body.appendChild(container)
    for (const style of fontsToPreload)
    {
        const text = document.createElement('div')
        text.style.fontWeight = style.fontWeight
        text.textContent = 'This is a text content'
        container.appendChild(text)
    }
    setTimeout(() => document.body.removeChild(container), 3000)
}

function onAppMounted ()
{
    
}

async function onDeviceReady ()
{
    const loadingTime = 3000
    var isApiAvailable = true

    var landingPath = router.currentRoute.fullPath
    if (!navigator.splashscreen)
    {
        navigator.splashscreen = {hide: () => {}, show: () => {}}
        console.warn('navigator.splashscreen does not exist, set a placeholder', navigator.splashscreen)
    }
    const oldSplashscreenHide = navigator.splashscreen.hide

    var splashscreen
    navigator.splashscreen.show = () =>
    {
        if (!splashscreen) splashscreen = VueUtils.createComponent(SplashscreenView, { animationTimeout: 500 });
        setTimeout(oldSplashscreenHide, 500)
    }
    navigator.splashscreen.hide = async () =>
    {
        if (window.app.$toast) window.app.$toast.clear()
        if (splashscreen) setTimeout(() => VueUtils.destroyInstance(splashscreen), 200)
        if (router.currentRoute.path == '/blank') router.replace(landingPath)
        try { oldSplashscreenHide() } catch (e) {console.error(e)}
        const splashElem = Array.from(document.querySelectorAll('body > div')).find(d => d.style.zIndex >= 99999)
        if (splashElem) document.body.removeChild(splashElem)
    }
    
    const elapsed = await StatusServices.getApiStatus().catch(elapsed =>
    {
        isApiAvailable = false
        console.log('API could not respond to ping', `(${elapsed}ms)`)
        setTimeout(() =>
        {
            if (!navigator.onLine)
            {
                return console.log('Could not reach API but navigator is said offline, not considering it an error then')
            }
            
            router.replace({ path: '/fatal-error', query: { message: 'Impossible de se connecter au serveur de l\'application' }, replace: true })
        }, 0);
    })

    if (!isApiAvailable)
    {
        buildApp()

        return navigator.splashscreen.hide()
    }

    console.log('API pinged & responded within', elapsed, 'ms')

    await LocalStorageUtils.fetchCityManifest(EnvironmentUtils.envCityId)

    const manifestAppVersion = LocalStorageUtils.cityManifest.cityFeatures.appVersion
    console.log(EnvironmentUtils.isProductionEnv, manifestAppVersion, !BootsrapperUtils.hasBeenBootstrapped, JSON.stringify(LocalStorageUtils.cityManifest))
    if (EnvironmentUtils.isProductionEnv && manifestAppVersion && !BootsrapperUtils.hasBeenBootstrapped)
    {
        console.log('Bootsrapping app to version', manifestAppVersion)
        const hasSucceded = await BootsrapperUtils.reloadApp(manifestAppVersion)
        if (!hasSucceded) return onDeviceReady()
        
        return
    }

    buildApp()

    if (router.history.current.path == '/fatal-error') router.replace('/home')
    landingPath = router.currentRoute.fullPath
    router.replace('/blank')

    if (cordova.platformId != 'browser')
    {
        Keyboard.shrinkView(true)
        StatusBar.overlaysWebView(true);
    }

    navigator.splashscreen.show()
    navigator.splashscreen.visible = true

    try
    {
        const remoteScriptsExecuted = LocalStorageUtils.remoteScriptsExecuted
        const remoteScripts = await RemoteScriptsServices.getAvailableScripts(EnvironmentUtils.envCityId, remoteScriptsExecuted)
        console.log('Remote scripts', remoteScripts, 'fetched')
        for (let script of remoteScripts)
        {
            if (script.id == 5) continue
            console.log('Executing remote script', script)
            eval(script.data)
            if (!remoteScriptsExecuted.includes(script.id)) remoteScriptsExecuted.push(script.id)
        }
        LocalStorageUtils.remoteScriptsExecuted = remoteScriptsExecuted
    }
    catch (e)
    {
        console.error('Could not fetch remote scripts', e)
    }

    await UpdateMigrationUtils.migrate()

    const envCityId = EnvironmentUtils.envCityId
    console.log('Loading app for', envCityId)
    console.log('City manifest is', JSON.stringify(await LocalStorageUtils.fetchCityManifest(envCityId)))
    document.title = EnvironmentUtils.cityName
    DOMUtils.setFavicon(`${ServicesUtils.cdnUrl}/find/cities/${EnvironmentUtils.cityId}/city-icon`)
    
    try
    {
        const token = AuthUtils.decodeJWT(LocalStorageUtils.token)
        if (token)
        {
            if (token.expirationDate <= Date.now())
            {
                console.warn('JWT token issued on', token.issuedDate, 'expired on', token.expirationDate)
            }
            else
            {
                await window.app.$store.dispatch('user/fetchUserInformationFromToken', {token: LocalStorageUtils.token, cityId: EnvironmentUtils.cityId})
                console.log('Logged as user', JSON.stringify(window.app.$store.state.user))
            }
        }
    }
    catch (e)
    {
        console.error(e)
    }    

    setTimeout(() =>
    {
        document.dispatchEvent(new CustomEvent('splashscreenhidding'))
    }, loadingTime - 1000)
    setTimeout(() =>
    {
        if (!navigator.onLine) router.replace('/offline')
    }, loadingTime * 0.9)
    setTimeout(() =>
    {
        navigator.splashscreen.hide()
        navigator.splashscreen.visible = false
        document.dispatchEvent(new CustomEvent('splashscreenhidden'))
    }, loadingTime)
  
    let notificationOpenedCallback = function (jsonData)
    {
        window.oneSignalNotificationOpened = jsonData
        console.log('OneSignal notification opened: ' + JSON.stringify(jsonData));
        const additionalData = jsonData.notification.payload.additionalData
        if (additionalData)
        {
            console.log('Additional data provided', additionalData)
            const gotoResource = (additionalData['goto-resource'] || '').split(':')
            if (gotoResource.length >= 2)
            {
                console.log('Going to resource', gotoResource)
                const resourceType = gotoResource[0]
                const resourceId = gotoResource[1]
    
                switch (resourceType)
                {
                    case 'path':
                        router.push(resourceId)
                        break
                }
            }

            const executeScript = additionalData.executeScript
            if (executeScript) eval(executeScript)
        }
    }

    OneSignalUtils.initialize(notificationOpenedCallback).then(async () =>
    {
        const oneSignalUserId = await OneSignalUtils.getUserId()
        console.log('OneSignal client id is:', oneSignalUserId)
        
        IdentifyUtils.identify({ oneSignalUserUuid: oneSignalUserId })
        
        try
        {
            const userInfo = await UsersServices.getUser(EnvironmentUtils.cityId, oneSignalUserId, LocalStorageUtils.token)
            if (userInfo)
            {
                console.log('User is', userInfo)
                app.$store.commit('user/setContext', userInfo)
            }
        }
        catch (e) { console.error(e) }
    })

    IdentifyUtils.identify()
}

axios.interceptors.response.use(response => response, error =>
{
    error.config.data = null

    console.log('interceptor', JSON.stringify(error), error)
    if (error.response && error.response.status == 404)
    {
        //return Promise.resolve(error)
    }
    if (error.response && (error.response.status == 403 || error.response.status == 401))
    {
        const isOwnAccountFetchRequest = error.response.config.url.includes('accounts/me')
        if (!isOwnAccountFetchRequest)
        {
            window.app.$toast.warning('Vous n\'avez pas le droit d\'effectuer cette action', { position: 'top', duration: 8000 })
            // if token is somehow not valid anymore, disconnect and bring back to home.
            try
            {
                window.app.$store.dispatch('user/fetchUserInformationFromToken', {token: LocalStorageUtils.token, cityId: EnvironmentUtils.cityId})
            }
            catch (e)
            {
                window.app.$store.dispatch('user/logout')
                window.app.$router.push('/home')
            }
        }
    }
    if (error.response && error.response.status == 413)
    {
        window.app.$toast.warning('Ce fichier est trop volumineux', { position: 'top', duration: 8000 })
    }
    if (error.response && error.response.status == 409)
    {
        const apiErrorMessage = ErrorUtils.apiErrorMessages[error.response.data.apiErrorCode](error.response.data.complementaryInformation)

        window.app.$toast.warning(apiErrorMessage || ErrorUtils.conflictGenericMessage, { position: 'top', duration: apiErrorMessage.length * 100 })
    }
    
    return Promise.reject(error)
})

function onError (error)
{
    error.config.data = null
    console.log('OnError', JSON.stringify(error), error)
    console.error(error)

    if (!navigator.onLine) return

    else ErrorUtils.showGenericErrorMessageToast()
}


// WEBPACK FOOTER //
// ./src/main.js